import React, { useEffect, useState } from 'react';
import { CaretRight, CaretLeft, FileText, FolderSimple, SpinnerGap, Check, ArrowSquareOut, X } from '@phosphor-icons/react';

interface ConfluencePanelProps {
  open: boolean;
  onClose: () => void;
  /** When true, the panel is gating an in-flight deck creation. Hides the close
   *  affordance and tightens copy ("Confluence is required to create a deck").
   *  Closing still discards the pending creation — the parent owns that state. */
  requireConnection?: boolean;
  confluenceConnections?: any[];
  confluenceSpaces?: any[];
  /** Non-null when the most-recent listSpaces call failed (e.g. Atlassian
   *  upstream 5xx). Distinguishes "truly empty" from "couldn't load". */
  confluenceSpacesError?: string | null;
  confluencePages?: any[];
  confluenceLoading?: boolean;
  selectedSpaceId?: string;
  onConnectConfluence?: () => void;
  onRetrySpaces?: () => void;
  onDisconnectConfluence?: (connId: string) => void;
  onSelectSpace?: (spaceId: string) => void;
  onReferencePage?: (pageId: string) => Promise<string | void> | string | void;
  onRemoveReference?: (pageId: string) => void;
  referencingPageIds?: Set<string>;
  confluenceExpandedPages?: Set<string>;
  confluenceChildPages?: Map<string, any[]>;
  confluenceLoadingChildren?: Set<string>;
  onToggleExpandPage?: (pageId: string) => void;
  onPreviewPage?: (pageId: string) => void;
  onConfirmDone?: (titles: string[]) => void;
  chatWidth?: number;
}

export default function ConfluencePanel({
  open, onClose, requireConnection = false,
  confluenceConnections = [], confluenceSpaces = [], confluenceSpacesError = null,
  confluencePages = [],
  confluenceLoading = false, selectedSpaceId = '',
  onConnectConfluence, onRetrySpaces, onDisconnectConfluence, onSelectSpace, onReferencePage, onRemoveReference,
  referencingPageIds = new Set(),
  confluenceExpandedPages = new Set(), confluenceChildPages = new Map(),
  confluenceLoadingChildren = new Set(),
  onToggleExpandPage, onConfirmDone,
}: ConfluencePanelProps) {
  const [view, setView] = useState<'spaces' | 'pages'>('spaces');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setView(selectedSpaceId ? 'pages' : 'spaces');
      setSelectedIds(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  const allPages = [...confluencePages, ...[...confluenceChildPages.values()].flat()];
  const referencedCount = allPages.filter(p => p.referenced).length;

  const handleSelectSpace = (spaceId: string) => {
    onSelectSpace?.(spaceId);
    setView('pages');
  };

  const toggleSelect = (pageId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedIds.size === 0 || !onReferencePage) return;
    setIsSubmitting(true);
    const titles: string[] = [];
    for (const pageId of selectedIds) {
      try {
        const title = await onReferencePage(pageId);
        if (title) titles.push(title);
      } catch {}
    }
    setSelectedIds(new Set());
    setIsSubmitting(false);
    onClose();
    onConfirmDone?.(titles);
  };

  const handleBackToSpaces = () => {
    setView('spaces');
    setSelectedIds(new Set());
  };

  const renderPageItem = (page: any, depth: number): React.ReactNode => {
    if (page.type === 'database') return null;
    const isExpanded = confluenceExpandedPages.has(page.id);
    const isLoadingChildren = confluenceLoadingChildren.has(page.id);
    const isReferenced = !!page.referenced;
    const isReferencing = referencingPageIds.has(page.id);
    const children = confluenceChildPages.get(page.id) || [];
    const isFolder = page.type === 'folder';
    const hasChildren = page.hasChildren !== false;
    const isSelected = selectedIds.has(page.id);

    return (
      <div key={page.id}>
        <div
          className="cp-row"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: `9px 28px 9px ${depth * 18 + 28}px`,
            cursor: 'default', fontSize: 'var(--fs-base)', fontWeight: 300,
            color: 'var(--text-secondary)',
            transition: 'background 0.12s ease',
          }}
        >
          {hasChildren ? (
            <span
              onClick={e => { e.stopPropagation(); onToggleExpandPage?.(page.id); }}
              style={{
                display: 'flex', alignItems: 'center', flexShrink: 0,
                cursor: 'pointer', color: 'var(--text-muted)',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.18s ease',
              }}
            >
              {isLoadingChildren
                ? <SpinnerGap size={11} weight="light" className="animate-spin" />
                : <CaretRight size={11} weight="light" />
              }
            </span>
          ) : (
            <span style={{ width: 11, flexShrink: 0 }} />
          )}

          {!isFolder && !isReferenced ? (
            <span
              onClick={e => { e.stopPropagation(); toggleSelect(page.id); }}
              style={{
                width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                border: `1px solid ${isSelected ? 'var(--brand)' : 'var(--border-strong)'}`,
                background: isSelected ? 'var(--brand)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              {isSelected && <Check size={9} weight="bold" style={{ color: '#fff' }} />}
            </span>
          ) : !isFolder && isReferenced ? (
            <span
              onClick={e => { e.stopPropagation(); onRemoveReference?.(page.id); }}
              title="Click to remove"
              style={{
                width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                border: '1px solid var(--text-muted)',
                background: 'transparent',
                color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              <Check size={9} weight="bold" />
            </span>
          ) : (
            <span style={{ width: 14, flexShrink: 0 }} />
          )}

          {isFolder
            ? <FolderSimple size={14} weight="light" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            : <FileText size={14} weight="light" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          }

          <span style={{
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: isReferenced ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontWeight: isReferenced ? 400 : 300,
          }} title={page.title}>{page.title}</span>

          {isReferencing ? (
            <SpinnerGap size={11} weight="light" className="animate-spin" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          ) : !isFolder && page.url ? (
            <a
              href={page.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              title="Open in Confluence"
              style={{
                display: 'flex', alignItems: 'center', flexShrink: 0,
                color: 'var(--text-muted)', opacity: 0,
                transition: 'opacity 0.15s ease',
              }}
              className="cp-link-btn"
            >
              <ArrowSquareOut size={12} weight="light" />
            </a>
          ) : null}
        </div>

        {isExpanded && (
          <>
            {isLoadingChildren && children.length === 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: `6px 28px 6px ${(depth + 1) * 18 + 28}px`,
                fontSize: 'var(--fs-sm)', fontWeight: 300, color: 'var(--text-muted)',
              }}>
                <SpinnerGap size={11} weight="light" className="animate-spin" />
                <span>Loading…</span>
              </div>
            )}
            {children.map((child: any) => renderPageItem(child, depth + 1))}
          </>
        )}
      </div>
    );
  };

  if (!open) return null;

  const currentSpace = confluenceSpaces.find(s => (s.id || s.key) === selectedSpaceId);
  const hasConnection = confluenceConnections.length > 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Backdrop — softer than before to keep the page visible behind */}
      <div
        onClick={requireConnection ? undefined : onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0, 0, 0, 0.32)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'relative',
        width: 560, maxWidth: '92vw',
        height: hasConnection ? 540 : 'auto',
        maxHeight: '78vh',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'cpPanelIn 0.18s ease',
      }}>
        {/* Header — calm, hairline divider, no bold logo */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 28px',
          borderBottom: hasConnection ? '1px solid var(--border)' : 'none',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {hasConnection && view === 'pages' && (
              <button
                onClick={handleBackToSpaces}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 24, height: 24, borderRadius: 6, border: 'none',
                  background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  marginLeft: -4,
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--bg-primary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                aria-label="Back to spaces"
              >
                <CaretLeft size={14} weight="light" />
              </button>
            )}
            <span style={{
              fontSize: 'var(--fs-md)',
              fontWeight: 400,
              color: 'var(--text-primary)',
              letterSpacing: '0.01em',
            }}>
              {hasConnection && view === 'pages' && currentSpace ? currentSpace.name : 'Confluence'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {hasConnection && !requireConnection && (
              <button
                onClick={() => onDisconnectConfluence?.(confluenceConnections[0]?.id)}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: 'none', background: 'transparent',
                  color: 'var(--text-muted)', cursor: 'pointer',
                  fontSize: 'var(--fs-xs)', fontWeight: 300,
                  fontFamily: 'inherit', transition: 'color 0.15s ease, background 0.15s ease',
                  letterSpacing: '0.02em',
                }}
                onMouseOver={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-primary)'; }}
                onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
              >
                Disconnect
              </button>
            )}
            {!requireConnection && (
              <button
                onClick={onClose}
                style={{
                  width: 24, height: 24, borderRadius: 6, border: 'none', background: 'transparent',
                  color: 'var(--text-muted)', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease',
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--bg-primary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                aria-label="Close"
              >
                <X size={14} weight="light" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        {!hasConnection ? (
          // Empty / connect state — zen: lots of whitespace, single CTA.
          <div style={{
            padding: '24px 48px 56px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
          }}>
            {/* Thin circle mark, neutral color — replaces the bright Atlassian gradient */}
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              border: '1px solid var(--border-strong)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-muted)',
              marginBottom: 24,
            }}>
              <FileText size={20} weight="thin" />
            </div>
            <div style={{
              fontSize: 'var(--fs-lg)', fontWeight: 300,
              color: 'var(--text-primary)',
              letterSpacing: '0.01em',
              marginBottom: 8,
            }}>
              {requireConnection ? 'Connect Confluence to begin' : 'Connect Confluence'}
            </div>
            <div style={{
              fontSize: 'var(--fs-sm)', fontWeight: 300,
              color: 'var(--text-muted)',
              maxWidth: 320, lineHeight: 1.6,
              marginBottom: 28,
            }}>
              {requireConnection
                ? 'PitchPilot draws on your Confluence pages to ground every deck in real source material. One connection, then the agent picks up automatically.'
                : 'Reference your team’s Atlassian pages in any deck.'}
            </div>
            <button
              onClick={onConnectConfluence}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 22px', borderRadius: 8,
                background: 'var(--brand)',
                color: '#fff',
                border: 'none', cursor: 'pointer',
                fontSize: 'var(--fs-sm)', fontWeight: 500,
                fontFamily: 'inherit', letterSpacing: '0.02em',
                transition: 'opacity 0.15s ease, transform 0.1s ease',
              }}
              onMouseOver={e => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
              onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; }}
              onMouseUp={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Connect
            </button>
            {requireConnection && (
              <button
                onClick={onClose}
                style={{
                  marginTop: 16,
                  padding: '6px 12px', borderRadius: 6,
                  background: 'transparent', border: 'none',
                  color: 'var(--text-muted)', cursor: 'pointer',
                  fontSize: 'var(--fs-xs)', fontWeight: 300,
                  fontFamily: 'inherit', letterSpacing: '0.02em',
                  transition: 'color 0.15s ease',
                }}
                onMouseOver={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              >
                Cancel
              </button>
            )}
          </div>
        ) : view === 'spaces' ? (
          // Spaces — hairline-divided list, not a card grid
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {confluenceSpaces.length === 0 ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100%', color: 'var(--text-muted)',
                fontSize: 'var(--fs-sm)', fontWeight: 300,
                gap: 12, padding: '0 48px', textAlign: 'center',
              }}>
                {confluenceLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SpinnerGap size={14} weight="light" className="animate-spin" />
                    <span>Loading spaces…</span>
                  </div>
                ) : confluenceSpacesError ? (
                  <>
                    <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      Confluence is temporarily unavailable. This usually clears on its own — try again in a moment.
                    </div>
                    {onRetrySpaces && (
                      <button
                        onClick={onRetrySpaces}
                        style={{
                          padding: '6px 16px', borderRadius: 6,
                          background: 'transparent', border: '1px solid var(--border-strong)',
                          color: 'var(--text-secondary)', cursor: 'pointer',
                          fontSize: 'var(--fs-sm)', fontWeight: 400,
                          fontFamily: 'inherit', letterSpacing: '0.02em',
                          transition: 'background 0.15s ease, color 0.15s ease',
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = 'var(--bg-primary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        Retry
                      </button>
                    )}
                  </>
                ) : 'No spaces found'}
              </div>
            ) : (
              <div>
                {confluenceSpaces.map((space: any, i: number) => (
                  <div
                    key={space.id || space.key}
                    className="cp-row"
                    onClick={() => handleSelectSpace(space.id || space.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '16px 28px',
                      cursor: 'pointer',
                      borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                      transition: 'background 0.12s ease',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-secondary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 'var(--fs-sm)', fontWeight: 400, flexShrink: 0,
                      letterSpacing: '0.04em',
                    }}>
                      {(space.name || 'S')[0].toUpperCase()}
                    </div>
                    <div style={{
                      flex: 1, minWidth: 0,
                      fontSize: 'var(--fs-base)', fontWeight: 400,
                      color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {space.name}
                    </div>
                    <CaretRight size={12} weight="light" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {confluenceLoading && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '16px 28px', fontSize: 'var(--fs-sm)', fontWeight: 300, color: 'var(--text-muted)',
                }}>
                  <SpinnerGap size={12} weight="light" className="animate-spin" />
                  <span>Loading pages…</span>
                </div>
              )}
              {!confluenceLoading && confluencePages.length === 0 && (
                <div style={{
                  fontSize: 'var(--fs-sm)', fontWeight: 300,
                  color: 'var(--text-muted)', padding: '16px 28px',
                }}>
                  No pages in this space.
                </div>
              )}
              {!confluenceLoading && confluencePages.map(page => renderPageItem(page, 0))}
            </div>

            <div style={{
              padding: '14px 28px',
              borderTop: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <span style={{
                fontSize: 'var(--fs-xs)', fontWeight: 300,
                color: 'var(--text-muted)', letterSpacing: '0.02em',
              }}>
                {referencedCount > 0 && (
                  <><span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{referencedCount}</span> referenced</>
                )}
                {selectedIds.size > 0 && (
                  <>{referencedCount > 0 ? ' · ' : ''}<span style={{ color: 'var(--text-primary)', fontWeight: 400 }}>{selectedIds.size}</span> selected</>
                )}
                {referencedCount === 0 && selectedIds.size === 0 && 'Select pages to reference'}
              </span>
              <button
                onClick={handleConfirm}
                disabled={selectedIds.size === 0 || isSubmitting}
                style={{
                  padding: '7px 18px',
                  fontSize: 'var(--fs-sm)', fontWeight: 500,
                  background: selectedIds.size > 0 ? 'var(--brand)' : 'transparent',
                  color: selectedIds.size > 0 ? '#fff' : 'var(--text-muted)',
                  border: selectedIds.size > 0 ? 'none' : '1px solid var(--border)',
                  borderRadius: 8,
                  cursor: selectedIds.size > 0 && !isSubmitting ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                  transition: 'opacity 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: 8,
                  opacity: isSubmitting ? 0.6 : 1,
                  letterSpacing: '0.02em',
                }}
              >
                {isSubmitting ? (
                  <><SpinnerGap size={12} weight="light" className="animate-spin" /> Importing…</>
                ) : (
                  <>Confirm{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}</>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes cpPanelIn {
          from { opacity: 0; transform: scale(0.97) translateY(4px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
        .cp-row:hover { background: var(--bg-primary); }
        .cp-row:hover .cp-link-btn { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
